import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { FiArrowLeft, FiList } from 'react-icons/fi';
import api from '../config/api';
import { usePermissions } from '../context/PermissionsContext';
import { usePageTitle } from '../context/PageChromeContext';
import IconButton from '../components/IconButton';
import LoadingButton from '../components/LoadingButton';
import { statusLabel } from '../components/shared/Ui';
import { queryKeys, staleTimes } from '../config/queryKeys';
import { appAlert } from '../utils/appDialog';
import { companyOptionLabel } from '../utils/legalCompany';
import './Services.css';
import '../components/erp/Erp.css';

const ACCOUNT_TYPES = ['asset', 'liability', 'equity', 'income', 'expense'];

/** Full-page new chart-of-accounts leaf or group. */
const GlAccountDetails = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission('erp_accounts_manage');

  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({
    code: '',
    name: '',
    account_type: 'expense',
    parent_id: '',
    is_group: false,
    company_id: searchParams.get('company_id') || '',
  });

  usePageTitle('New GL account');

  const companies = useQuery(
    queryKeys.accountingCompanies,
    async () => (await api.get('/accounting/companies')).data,
    { enabled: canManage, staleTime: staleTimes.reference }
  );

  useEffect(() => {
    if (form.company_id) return;
    const rows = companies.data || [];
    const fallback = rows.find((c) => c.is_default) || rows[0];
    if (fallback?.id) setForm((prev) => ({ ...prev, company_id: fallback.id }));
  }, [companies.data, form.company_id]);

  const accounts = useQuery(
    [...queryKeys.accountingAccounts, form.company_id || 'none'],
    async () =>
      (
        await api.get('/accounting/accounts', {
          params: form.company_id ? { company_id: form.company_id } : undefined,
        })
      ).data,
    { enabled: canManage && Boolean(form.company_id) }
  );

  const parentOptions = (accounts.data || []).filter((a) => a.is_group);

  const createMutation = useMutation(
    async () =>
      (
        await api.post('/accounting/accounts', {
          code: form.code,
          name: form.name,
          account_type: form.account_type,
          parent_id: form.parent_id || null,
          is_group: form.is_group ? 1 : 0,
          company_id: form.company_id || undefined,
        })
      ).data,
    {
      onSuccess: () => {
        qc.invalidateQueries(queryKeys.accountingAccounts);
        navigate('/erp/accounting?tab=Accounts', { replace: true });
      },
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );

  const back = () => navigate('/erp/accounting?tab=Accounts');

  if (!canManage) {
    return (
      <div className="svc-package-page list-page erp-page">
        <div className="svc-package-toolbar page-toolbar">
          <IconButton icon={FiArrowLeft} label="Back" onClick={back} />
        </div>
        <div className="svc-package-page-card">
          <div className="error">You do not have permission to manage accounts.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="svc-package-page list-page erp-page">
      <div className="svc-package-toolbar page-toolbar">
        <IconButton icon={FiArrowLeft} label="Back to chart of accounts" onClick={back} />
      </div>
      <div className="svc-package-page-card">
        <div className="svc-package-page-header">
          <h1>
            <FiList aria-hidden /> New account
          </h1>
          <p className="page-subtitle">
            Leaf accounts become postable in the journal editor. Groups organize the chart for
            the selected legal company.
          </p>
        </div>
        <form
          className="svc-package-form"
          onSubmit={(e) => {
            e.preventDefault();
            createMutation.mutate();
          }}
        >
          <div className="erp-form-grid">
            <div className="erp-field">
              <label htmlFor="acct-company">Legal company</label>
              <select
                id="acct-company"
                required
                value={form.company_id}
                onChange={(e) => setForm({ ...form, company_id: e.target.value, parent_id: '' })}
              >
                <option value="">Select…</option>
                {(companies.data || []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {companyOptionLabel(c)}
                  </option>
                ))}
              </select>
            </div>
            <div className="erp-field">
              <label htmlFor="acct-code">Code</label>
              <input
                id="acct-code"
                required
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="6100"
              />
            </div>
            <div className="erp-field">
              <label htmlFor="acct-name">Name</label>
              <input
                id="acct-name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Software subscriptions"
              />
            </div>
            <div className="erp-field">
              <label htmlFor="acct-type">Type</label>
              <select
                id="acct-type"
                value={form.account_type}
                onChange={(e) => setForm({ ...form, account_type: e.target.value })}
              >
                {ACCOUNT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {statusLabel(t)}
                  </option>
                ))}
              </select>
            </div>
            <div className="erp-field">
              <label htmlFor="acct-parent">Parent (optional)</label>
              <select
                id="acct-parent"
                value={form.parent_id}
                onChange={(e) => setForm({ ...form, parent_id: e.target.value })}
              >
                <option value="">— Top level —</option>
                {parentOptions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.code} · {p.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="erp-field erp-field--check">
              <label htmlFor="acct-group">
                <input
                  id="acct-group"
                  type="checkbox"
                  checked={Boolean(form.is_group)}
                  onChange={(e) => setForm({ ...form, is_group: e.target.checked })}
                />
                Group account (not postable)
              </label>
            </div>
          </div>
          <div className="svc-package-page-actions">
            <button type="button" className="btn btn-secondary" onClick={back}>
              Cancel
            </button>
            <LoadingButton
              type="submit"
              className="btn btn-primary"
              loading={createMutation.isLoading}
              disabled={!form.code || !form.name || !form.company_id}
            >
              Add account
            </LoadingButton>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GlAccountDetails;
